import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import MatchIndustrySection from "../../components/employer/MatchIndustrySection";
import EmployerFAQSection from "../../components/employer/EmployerFAQSection";

const features = [
    {
        title: "Get matched",
        description: "We recommend the best-fit doctors based on role, experience, and clinical speciality.",
        icon: "/Get matched.svg",
    },
    {
        title: "Protect your budget",
        description: "Agencies can mark up talent by 60-70%. With Doctors Jobs, first listings are free, and premium plans stretch your hiring budget further.",
        icon: "/Protect your budget.svg",
    },
    {
        title: "Hire faster, hire smarter",
        description: "Save hours on screening and coordination. Focus your time where it matters, building the best possible Medical care team.",
        icon: "/Hire faster, hire smarter.svg",
    },
];

const EmployerLandingPage = () => {
    const { user } = useAuth();

    return (
        <>
        <style>{`
            @media (max-width: 768px) {
                .employer-landing-h1 {
                    font-size: 24px !important;
                }
                .employer-how-it-works-section {
                    margin-top: -5rem !important;
                }
                .employer-interview-heading {
                    font-size: 24px !important;
                }
                .employer-heading-h2 {
                    font-size: 26px !important;
                }
                .employer-heading-h3 {
                    font-size: 24px !important;
                }
                .employer-hero-text {
                    text-align: left !important;
                }
                .employer-hero-text br {
                    display: block;
                    margin-top: 0.75rem;
                }
                .employer-hero-second-line {
                    display: block;
                    margin-top: 0.75rem;
                }
            }
            @media (min-width: 768px) and (max-width: 1024px) {
                .employer-feature-cards {
                    grid-template-columns: 1fr !important;
                    gap: 1rem !important;
                }
                .employer-feature-card {
                    display: flex;
                    flex-direction: row;
                    align-items: flex-start;
                    gap: 1rem;
                }
                .employer-feature-card-icon {
                    flex-shrink: 0;
                    margin-bottom: 0 !important;
                }
                .employer-feature-card-content {
                    flex: 1;
                }
                .employer-feature-card h3 {
                    margin-top: 0 !important;
                }
            }
        `}</style>
        <main className="bg-[#f4f6ff] min-h-screen font-sans" style={{ fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif' }}>
            {/* Hero Section */}
            <section className="max-w-7xl mx-auto px-6 pt-16 pb-20 -mt-6 sm:-mt-8">
                <div className="text-center max-w-4xl mx-auto">
                    <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6 employer-landing-h1">
                        Hire doctors
                    </h1>
                    <p className="text-lg md:text-xl text-gray-700 leading-relaxed employer-hero-text">
                        Doctors Jobs gives you everything you need to find, match, and hire medical professionals.
                        <br />
                        <span className="employer-hero-second-line">All without the hassle or high cost of agencies. Hire confidently. Save time. Focus on care.</span>
                    </p>
                </div>
            </section>

            {/* Feature Cards */}
            <section className="max-w-7xl mx-auto px-6 pb-20">
                <div className="grid md:grid-cols-3 gap-8 employer-feature-cards">
                    {features.map((feature, index) => (
                        <div
                            key={index}
                            className="bg-gray-50 rounded-[32px] p-8 shadow-sm border border-gray-100 employer-feature-card"
                        >
                            <div className="text-gray-800 mb-4 employer-feature-card-icon">
                                <img src={feature.icon} alt={feature.title} className="w-8 h-8" />
                            </div>
                            <div className="employer-feature-card-content">
                                <h3 className="text-xl font-bold text-gray-900 mb-3">
                                    {feature.title}
                                </h3>
                                <p className="text-base text-gray-700 leading-relaxed">
                                    {feature.description}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* How it works */}
            <section className="bg-[#f4f6ff] employer-how-it-works-section">
                <div className="max-w-6xl mx-auto px-6 py-24 space-y-14">
                    <div className="text-center">
                        <h2 className="text-4xl lg:text-5xl font-normal text-gray-900 employer-heading-h2">Here's how it works</h2>
                    </div>

                    <div className="flex flex-col lg:flex-row items-center gap-12">
                        <div className="flex-1 space-y-6">
                            <div className="w-12 h-12 border-2 border-gray-900 rounded-full flex items-center justify-center text-xl font-semibold text-gray-900">
                                1
                            </div>
                            <h3 className="text-4xl lg:text-5xl font-normal text-gray-900 employer-heading-h3">
                                Sign up &amp; post a job
                            </h3>
                            <p className="text-lg text-gray-700">
                                Create your company profile and publish your first job,
                                <br />
                                free of charge.
                            </p>
                            <Link
                                to="/signup"
                                className="inline-flex items-center justify-center rounded-full bg-gray-800 px-6 py-3 text-white font-semibold shadow-sm hover:bg-gray-900 transition"
                            >
                                Get matched to talent
                            </Link>
                        </div>
                        <div className="flex-1 flex justify-center">
                            <div className="w-full max-w-xl">
                                <img
                                    src="/Sign up & post a job.webp"
                                    alt="Sign up & post a job"
                                    className="w-full rounded-3xl border-0 border-none outline-none"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-col-reverse lg:flex-row items-center gap-12 pt-10">
                        <div className="flex-1 flex justify-center">
                            <div className="w-full max-w-lg">
                                <img
                                    src="/Review candidates updated again.webp"
                                    alt="Candidate cards preview"
                                    className="w-full rounded-3xl border-0 border-none outline-none"
                                />
                            </div>
                        </div>
                        <div className="flex-1 space-y-6">
                            <div className="w-12 h-12 border-2 border-gray-900 rounded-full flex items-center justify-center text-xl font-semibold text-gray-900">
                                2
                            </div>
                            <h3 className="text-4xl lg:text-5xl font-normal text-gray-900 employer-heading-h3">
                                Review candidates
                            </h3>
                            <p className="text-lg text-gray-700 max-w-xl">
                                Receive applications and recommendations from verified doctors aligned with your job requirements.
                            </p>
                        </div>
                    </div>

                    <div className="flex flex-col lg:flex-row items-center gap-12 pt-10 pb-10 border-b border-white/40">
                        <div className="flex-1 space-y-6">
                            <div className="w-12 h-12 border-2 border-gray-900 rounded-full flex items-center justify-center text-xl font-semibold text-gray-900">
                                3
                            </div>
                            <h3 className="text-4xl lg:text-5xl font-normal text-gray-900 whitespace-nowrap employer-interview-heading">
                                Interview the&nbsp;&nbsp;stand-outs
                                <br className="hidden lg:block" />
                            </h3>
                            <p className="text-lg text-gray-700 max-w-xl">
                                Message top matches directly. Track and manage every applicant in your hiring pipeline,
                                all in one place.
                            </p>
                        </div>
                        <div className="flex-1 flex justify-center">
                            <div className="w-full max-w-lg">
                                <img
                                    src="/Interview the stand-outs updated.webp"
                                    alt="Interview the stand-outs preview"
                                    className="w-full rounded-3xl border-0 border-none outline-none"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="flex flex-col lg:flex-row items-center gap-12 pt-12">
                        <div className="flex-1 flex justify-center">
                            <div className="w-full max-w-xl">
                                <img
                                    src="/Hire quality healthcare talent.webp"
                                    alt="Hire quality healthcare talent"
                                    className="w-full rounded-[40px] border-none outline-none"
                                />
                            </div>
                        </div>
                        <div className="flex-1 space-y-8">
                            <div className="w-12 h-12 border-2 border-gray-900 rounded-full flex items-center justify-center text-xl font-semibold text-gray-900">
                                4
                            </div>
                            <h3 className="text-4xl lg:text-5xl font-normal text-gray-900 employer-heading-h3">
                                Hire quality
                                <br />
                                healthcare talent
                            </h3>
                            <p className="text-lg text-gray-700 max-w-xl">
                                We only accept doctors with verified HPCSA numbers to ensure your team gains skilled,
                                dependable professionals ready to deliver care.
                            </p>
                            <Link
                                to="/signup"
                                className="inline-flex items-center justify-center rounded-full bg-gray-900 px-7 py-3.5 text-base text-white font-semibold shadow-lg hover:bg-black transition"
                            >
                                Hire now
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </main>
        <MatchIndustrySection />
        <EmployerFAQSection />
        </>
    );
};

export default EmployerLandingPage;

